import IconSuccessSolid from '@apollo/icons/default/IconSuccessSolid.svg';
import IconWarningSolid from '@apollo/icons/default/IconWarningSolid.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import { printSchema } from 'graphql';
import React, { useCallback, useMemo, useState } from 'react';

import { useSchemaFromEndpointIntrospection } from 'src/app/graph/explorerPage/hooks/useExplorerState/useSchema/useSchemaFromEndpointIntrospection/useSchemaFromEndpointIntrospection';
import { URLField } from 'src/components/urlField/URLField';
import { format } from 'src/lib/format';
import { isLocalUrl } from 'src/lib/isLocalUrl';

export const LocalIntrospectionModal = ({
  isOpen,
  onClose,
  setSchemaUploaded,
  setSdl,
  stableHeaders,
}: {
  isOpen: boolean;
  onClose: () => void;
  setSchemaUploaded: (value: boolean) => void;
  setSdl: (sdl: string) => void;
  stableHeaders: Record<string, string>;
}) => {
  const [localEndpointUrl, setLocalEndpointUrl] = useState('');

  const {
    schema: schemaFromIntrospection,
    subgraphSdl: subgraphSdlFromIntrospection,
    isLoading,
    introspectionFailureType,
    introspectionResponseErrors,
  } = useSchemaFromEndpointIntrospection({
    includeCookies: 'cycle-on-unreachable',
    pollInterval: 3000,
    uri: localEndpointUrl,
    stableHeaders,
    pollForSubgraphSdlFirst: true,
    updateOnConfigChange: true,
  });

  const sdlFromIntrospection = useMemo(
    () =>
      subgraphSdlFromIntrospection ??
      (schemaFromIntrospection
        ? printSchema(schemaFromIntrospection)
        : undefined),
    [schemaFromIntrospection, subgraphSdlFromIntrospection],
  );

  const endpointIsLocal = useMemo(
    () => isLocalUrl(localEndpointUrl),
    [localEndpointUrl],
  );

  const introspectionHelper = useMemo<
    | {
        type: 'error' | 'helper';
        message: React.ReactNode;
        icon?: React.ReactNode;
      }
    | undefined
  >(() => {
    if (!introspectionFailureType && !endpointIsLocal) {
      return {
        type: 'error',
        message: `This is not a local endpoint. If you have a deployed graphql endpoint already, you can add it directly in the Endpoint URL field outside of this modal.`,
      };
    } else if (isLoading && localEndpointUrl.length > 0) {
      return {
        type: 'helper',
        message: 'Attempting to establish connection with your endpoint.',
        icon: <Spinner size="tiny" />,
      };
    } else if (introspectionFailureType === 'unableToReachEndpoint') {
      return {
        type: 'error',
        message: `We couldn't connect to that URL. Please double-check that it's correct.`,
      };
    } else if (
      introspectionFailureType === 'invalidIntrospectionResponse' ||
      introspectionFailureType === 'invalidIntrospectionSchema'
    ) {
      return {
        type: 'error',
        message:
          'We got an invalid introspection response from this endpoint. Please check the URL or add required headers in the previous screen.',
      };
    } else if (!introspectionFailureType && sdlFromIntrospection) {
      return {
        type: 'helper',
        icon: (
          <IconSuccessSolid className="mr-2 mt-0.5 size-4 text-icon-success" />
        ),
        message: `Connection succeeded and we have your${
          subgraphSdlFromIntrospection ? ' subgraph' : ''
        } schema!`,
      };
    } else if (introspectionFailureType === 'introspectionDisabled') {
      return {
        type: 'helper',
        icon: (
          <IconWarningSolid className="mr-2 mt-0.5 size-4 text-icon-warning" />
        ),
        message: (
          <>
            Connection succeeded, but we didn't receive your schema! Please
            resolve the following{' '}
            {format.pluralize(introspectionResponseErrors?.length, 'error')}:{' '}
            {introspectionResponseErrors?.length ? (
              <>
                <span className="italic">
                  "{introspectionResponseErrors.map((error) => error.message)}"
                </span>{' '}
                or directly upload your schema below.
              </>
            ) : (
              <p className="font-semibold">Please upload it below.</p>
            )}
          </>
        ),
      };
    }

    return undefined;
  }, [
    endpointIsLocal,
    isLoading,
    localEndpointUrl.length,
    introspectionFailureType,
    sdlFromIntrospection,
    subgraphSdlFromIntrospection,
    introspectionResponseErrors,
  ]);

  const uploadSchema = useCallback(() => {
    if (sdlFromIntrospection) {
      setSdl(sdlFromIntrospection);
      setSchemaUploaded(true);
      onClose();
    }
  }, [sdlFromIntrospection, setSdl, setSchemaUploaded, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Local introspection</ModalTitle>
          <ModalDescription>
            Do a <span className="italic">one-time</span> introspection of your
            schema. Provide a local URL and Studio will fetch your schema for
            you.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <FormControl
            isInvalid={introspectionHelper?.type === 'error'}
            isRequired
          >
            <FormLabel>Local URL</FormLabel>
            <URLField
              // only don't auto focus so folks can choose to open the advanced options
              // dropdown and upload a schema instead of use the endpoint
              autoFocus={true}
              name="unregisteredEndpointUrl"
              onChange={setLocalEndpointUrl}
              placeholder="Add your GraphQL endpoint"
              value={localEndpointUrl}
              isSocket={false}
            />
            <FormErrorMessage>{introspectionHelper?.message}</FormErrorMessage>
            {introspectionHelper?.type === 'helper' && (
              <FormHelperText>
                {introspectionHelper.icon}
                {introspectionHelper.message}
              </FormHelperText>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button onClick={onClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              onClick={uploadSchema}
              type="button"
              variant="primary"
              isDisabled={
                !endpointIsLocal ||
                !!(introspectionFailureType || !sdlFromIntrospection)
              }
            >
              Add schema
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
